import { Component, ElementRef, EventEmitter, Input, OnChanges, AfterViewInit, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { isString, noop } from 'rev-shared/util';


export enum VbUiCheckboxState {
	CHECKED = 'checked',
	MIXED = 'mixed',
	UNCHECKED = 'unchecked'
}

/**
 * Checkbox common component. Supports either 2 or 3 states. Input may either be a boolean or of type VbUiCheckboxState.
 */
@Component({
	selector: 'vb-ui-checkbox',
	exportAs: 'vbUiCheckbox',
	imports: [CommonModule, FormsModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VbUiCheckboxComponent),
			multi: true
		}
	],
	host: {
		'(keyup.space)': 'onClick()',
		'(keyup.enter)': 'onClick()'
	},
	templateUrl: './VbUiCheckbox.Component.html',
	styleUrl: './VbUiCheckbox.Component.ng.less'
})
export class VbUiCheckboxComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
	@Input() public accessibilityLabel: string;
	@Input() public mixedStateEnabled: boolean;
	@Input() public autoFocus: boolean;
	@Input() public disableTabFocus: boolean;
	@Input() public disabled: boolean;
	@Output() public touched = new EventEmitter<void>();
	@ViewChild('wrapperLabel', { static: true }) public wrapperLabel: ElementRef;

	public readonly VbUiCheckboxState = VbUiCheckboxState;

	private _currentState: VbUiCheckboxState = VbUiCheckboxState.UNCHECKED;
	private onChangeCallback: (value: any) => void = noop;
	private onTouchedCallback: () => void = noop;

	constructor(
		private readonly el: ElementRef
	) { }

	public ngAfterViewInit(): void {
		if (!this.accessibilityLabel && !this.el.nativeElement.innerText) {
			console.log('VbUiCheckboxComponent: accessibilityLabel is required: ', this.el.nativeElement);
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.autoFocus && this.autoFocus) {
			this.setAutoFocus();
		}
	}

	public onClick(): void {
		this.currentState = this._currentState === VbUiCheckboxState.UNCHECKED
			? VbUiCheckboxState.CHECKED
			: VbUiCheckboxState.UNCHECKED;
	}

	public onTouched(): void {
		this.touched.emit();
		this.onTouchedCallback();
	}

	public registerOnChange(fn: any): void {
		this.onChangeCallback = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouchedCallback = fn;
	}

	public writeValue(value: any): void {
		if (isString(value)) {
			this._currentState = value as VbUiCheckboxState;
		} else {
			this._currentState = this.valueToState(value);
		}
	}

	public get ariaCheckedState(): boolean | 'mixed' {
		return this.currentState === VbUiCheckboxState.MIXED
			? 'mixed'
			: this.currentState === VbUiCheckboxState.CHECKED;
	}

	public get currentState(): VbUiCheckboxState {
		return this._currentState;
	}

	public set currentState(value: VbUiCheckboxState) {
		if (value !== this._currentState) {
			this._currentState = value;
			this.onChangeCallback(this.mixedStateEnabled ? value : this.stateToChecked(value));
			this.onTouched();
		}
	}

	private stateToChecked(value: VbUiCheckboxState): boolean {
		return value === VbUiCheckboxState.CHECKED;
	}

	private valueToState(value: any): VbUiCheckboxState {
		return value
			? VbUiCheckboxState.CHECKED
			: VbUiCheckboxState.UNCHECKED;
	}

	private setAutoFocus(): void {
		this.wrapperLabel?.nativeElement?.focus();
	}
}
